// Player accounts: Firebase Auth for identity only (Tiny Legends' own project), plus the
// billing profile at Firestore users/{uid}.accountInfo that checkout reads.
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider, browserLocalPersistence, browserSessionPersistence, createUserWithEmailAndPassword, getAuth,
  onAuthStateChanged, reload, sendEmailVerification, sendPasswordResetEmail, setPersistence, signInWithEmailAndPassword,
  signInWithPopup, signOut, updateProfile, type Auth, type User,
} from 'firebase/auth';
import { doc, getDoc, getFirestore, serverTimestamp, setDoc, type Firestore } from 'firebase/firestore';
import { api, ApiError } from './api';
import { FIREBASE_CONFIG } from './config';

/** Billing profile. `country` is ISO 3166 alpha-2: the payment gateway rejects display names. */
export interface AccountInfo {
  firstName: string;
  lastName: string;
  address: string;
  phone: string;
  country: string;
  zipCode: string;
}

export const EMPTY_ACCOUNT_INFO: AccountInfo = { firstName: '', lastName: '', address: '', phone: '', country: '', zipCode: '' };

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const MIN_PASSWORD = 6;

type Listener = (user: User | null) => void;

export class Accounts {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  private listeners = new Set<Listener>();
  private firstState: Promise<User | null>;

  constructor() {
    this.app = initializeApp(FIREBASE_CONFIG);
    this.auth = getAuth(this.app);
    this.db = getFirestore(this.app);
    this.firstState = new Promise((resolve) => {
      const stop = onAuthStateChanged(this.auth, (u) => { stop(); resolve(u); });
    });
    onAuthStateChanged(this.auth, (u) => this.listeners.forEach((l) => l(u)));
  }

  /** The signed-in user once Firebase has restored the session (or null for a guest). */
  ready(): Promise<User | null> { return this.firstState; }
  get user(): User | null { return this.auth.currentUser; }
  get isGuest(): boolean { return !this.auth.currentUser; }
  onChange(l: Listener): () => void { this.listeners.add(l); return () => this.listeners.delete(l); }

  /** A current ID token for Worker calls; the SDK refreshes it as it nears expiry. */
  readonly token = async (): Promise<string> => {
    const u = this.auth.currentUser;
    if (!u) throw new ApiError('auth/no-current-user', 'Please sign in first.', 401);
    return u.getIdToken();
  };

  async signIn(email: string, password: string, remember: boolean): Promise<User> {
    await setPersistence(this.auth, remember ? browserLocalPersistence : browserSessionPersistence);
    return (await signInWithEmailAndPassword(this.auth, email.trim(), password)).user;
  }

  async signInWithGoogle(): Promise<User> {
    return (await signInWithPopup(this.auth, new GoogleAuthProvider())).user;
  }

  /** Creates the account, names it, and sends the verification mail (a mail failure never blocks sign-up). */
  async signUp(name: string, email: string, password: string): Promise<User> {
    const { user } = await createUserWithEmailAndPassword(this.auth, email.trim(), password);
    await updateProfile(user, { displayName: name.trim() });
    await reload(user);
    await this.sendVerification().catch((e) => console.warn('Verification mail not sent:', e));
    return user;
  }

  /** Branded mail through the Worker; Firebase's own mail only if the Worker is down (not if it refused). */
  async sendVerification(): Promise<void> {
    const u = this.auth.currentUser;
    if (!u) throw new ApiError('auth/no-current-user', 'Please sign in first.', 401);
    try {
      await api('/v1/auth/send-verification', { body: {}, token: this.token });
    } catch (e) {
      if (e instanceof ApiError && e.transient) await sendEmailVerification(u);
      else throw e;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      await api('/v1/auth/forgot-password', { body: { email: email.trim() } });
    } catch (e) {
      if (e instanceof ApiError && e.transient) await sendPasswordResetEmail(this.auth, email.trim());
      else throw e;
    }
  }

  /** Re-reads the user and refreshes the token so the verified flag reaches the server too. */
  async checkVerified(): Promise<boolean> {
    const u = this.auth.currentUser;
    if (!u) return false;
    await reload(u);
    if (u.emailVerified) await u.getIdToken(true);
    return u.emailVerified;
  }

  signOut(): Promise<void> { return signOut(this.auth); }

  async loadAccountInfo(): Promise<AccountInfo> {
    const u = this.auth.currentUser;
    if (!u) return { ...EMPTY_ACCOUNT_INFO };
    try {
      const snap = await getDoc(doc(this.db, 'users', u.uid));
      const info = (snap.data()?.accountInfo ?? {}) as Partial<AccountInfo>;
      const out = { ...EMPTY_ACCOUNT_INFO };
      for (const k of Object.keys(out) as (keyof AccountInfo)[]) if (typeof info[k] === 'string') out[k] = info[k]!;
      return out;
    } catch (e) {
      console.warn('Account info not loaded:', e);
      return { ...EMPTY_ACCOUNT_INFO };
    }
  }

  async saveAccountInfo(info: AccountInfo): Promise<void> {
    const u = this.auth.currentUser;
    if (!u) throw new ApiError('auth/no-current-user', 'Please sign in first.', 401);
    await setDoc(doc(this.db, 'users', u.uid), { accountInfo: { ...info, updatedAt: serverTimestamp() } }, { merge: true });
  }
}

/** A real account: email confirmed, or signed in with Google (whose emails are already verified). */
export function isVerifiedUser(u: User): boolean {
  return u.emailVerified || u.providerData.some((p) => p.providerId === 'google.com');
}

/** The name to greet a player by: their display name, else their email's local part, title-cased. */
export function displayNameOf(u: User): string {
  if (u.displayName?.trim()) return u.displayName.trim();
  const local = (u.email ?? '').split('@')[0] ?? '';
  return local.replace(/[._]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || 'Player';
}

/** Player-facing text for an auth or API error. */
export function friendlyError(e: unknown): string {
  const code = (e as { code?: string })?.code ?? '';
  const map: Record<string, string> = {
    'auth/invalid-email': "That email address doesn't look right.",
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect email or password.',
    'auth/invalid-credential': 'Incorrect email or password.',
    'auth/invalid-login-credentials': 'Incorrect email or password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': `Password is too weak. Use at least ${MIN_PASSWORD} characters.`,
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/network-request-failed': 'Network error. Check your connection.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
    'auth/popup-blocked': 'Popup blocked by the browser. Please allow popups.',
    'auth/unauthorized-domain': 'Google sign-in is not enabled for this address yet.',
    rate_limited: 'Too many requests. Please wait a moment and try again.',
    network: 'Network error. Check your connection.',
  };
  if (map[code]) return map[code]!;
  const msg = (e as { message?: string })?.message;
  return msg && !/^Firebase:/.test(msg) ? msg : 'Something went wrong. Please try again.';
}
